import React, { useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, ClickAwayListener } from "@material-ui/core";
import FilterButton from "./FilterButton";
import FilterDimensionMenu from "./FilterDimensionMenu";
import FilterValueSelector from "./FilterValueSelector";
import FilterChipBar from "./FilterChipBar";

export default function FilterSystem({
  dimensions,
  filterOptions,
  selectedFilters,
  onFiltersChange,
  clientColor,
  disabled = false,
}) {
  const filterButtonRef = useRef(null);
  const dimensionMenuRef = useRef(null);
  const [dimensionMenuAnchorEl, setDimensionMenuAnchorEl] = useState(null);
  const [currentDimension, setCurrentDimension] = useState(null);

  // Open dimension menu
  const handleFilterButtonClick = () => {
    setDimensionMenuAnchorEl(filterButtonRef.current);
  };

  // Close dimension menu
  const handleCloseDimensionMenu = () => {
    setDimensionMenuAnchorEl(null);
    setCurrentDimension(null);
  };

  // When dimension is selected, open value selector
  const handleDimensionSelect = (dimension) => {
    setCurrentDimension(dimension);
  };

  // Apply filter values for the selected dimension
  const handleApplyFilter = useCallback(
    (values) => {
      if (!currentDimension) return;

      const newFilters = {
        ...selectedFilters,
        [currentDimension.key]: values,
      };

      onFiltersChange(newFilters);

      // Close popovers
      setCurrentDimension(null);
      setDimensionMenuAnchorEl(null);
    },
    [currentDimension, selectedFilters, onFiltersChange],
  );

  // Remove a single filter value
  const handleRemoveFilter = useCallback(
    (dimensionKey, value) => {
      const currentValues = selectedFilters[dimensionKey] || [];
      const newValues = currentValues.filter((v) => v !== value);

      const newFilters = { ...selectedFilters };

      // If no values left, remove the key entirely instead of empty array
      if (newValues.length === 0) {
        delete newFilters[dimensionKey];
      } else {
        newFilters[dimensionKey] = newValues;
      }

      onFiltersChange(newFilters);
    },
    [selectedFilters, onFiltersChange],
  );

  // Clear all values for a dimension
  const handleClearDimension = useCallback(
    (dimensionKey) => {
      const newFilters = { ...selectedFilters };

      // Remove the key entirely instead of setting to empty array
      delete newFilters[dimensionKey];

      onFiltersChange(newFilters);
    },
    [selectedFilters, onFiltersChange],
  );

  // Close value selector
  const handleCloseValueSelector = () => {
    setCurrentDimension(null);
  };

  // Count active filters
  const activeFiltersCount = Object.keys(selectedFilters).reduce(
    (count, key) => {
      return count + (selectedFilters[key]?.length || 0);
    },
    0,
  );

  // Get active dimension keys (for highlighting in menu)
  const activeDimensionKeys = Object.keys(selectedFilters).filter(
    (key) => selectedFilters[key] && selectedFilters[key].length > 0,
  );

  // Create dimension labels map
  const dimensionLabels = dimensions.reduce((acc, dim) => {
    acc[dim.key] = dim.label;
    return acc;
  }, {});

  // Handle click away - only close if clicking outside both popovers
  const handleClickAway = (event) => {
    // Don't close if clicking inside either popover
    if (
      dimensionMenuAnchorEl?.contains(event.target) ||
      dimensionMenuRef.current?.contains(event.target)
    ) {
      return;
    }
    handleCloseDimensionMenu();
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        display="flex"
        alignItems="flex-start"
        gap={0}
        style={{ position: "relative", flex: 1, minWidth: 0 }}
      >
        {/* Filter Button - fixed position */}
        <Box
          style={{
            position: "relative",
            zIndex: 1,
            flexShrink: 0,
            paddingTop: "8px",
          }}
        >
          <span ref={filterButtonRef}>
            <FilterButton
              onClick={handleFilterButtonClick}
              activeFiltersCount={activeFiltersCount}
              clientColor={clientColor}
              disabled={disabled}
            />
          </span>
        </Box>

        {/* Filter Chips Display - wrappable container */}
        <Box
          style={{
            flex: 1,
            minWidth: 0,
            paddingLeft: "16px",
            paddingTop: "8px",
          }}
        >
          <FilterChipBar
            filters={selectedFilters}
            dimensionLabels={dimensionLabels}
            onRemoveFilter={handleRemoveFilter}
            onClearDimension={handleClearDimension}
            clientColor={clientColor}
          />
        </Box>

        {/* Dimension Selection Menu (First Popover) */}
        <FilterDimensionMenu
          open={Boolean(dimensionMenuAnchorEl)}
          anchorEl={dimensionMenuAnchorEl}
          dimensions={dimensions}
          onDimensionSelect={handleDimensionSelect}
          activeDimensions={activeDimensionKeys}
          selectedFilters={selectedFilters}
          clientColor={clientColor}
          ref={dimensionMenuRef}
        />

        {/* Value Selector (Cascading Popover) */}
        <FilterValueSelector
          open={Boolean(currentDimension)}
          anchorEl={dimensionMenuRef.current}
          selectedDimension={currentDimension}
          options={
            currentDimension ? filterOptions[currentDimension.key] || [] : []
          }
          selected={
            currentDimension ? selectedFilters[currentDimension.key] || [] : []
          }
          onApply={handleApplyFilter}
          onClose={handleCloseValueSelector}
          clientColor={clientColor}
        />
      </Box>
    </ClickAwayListener>
  );
}

FilterSystem.propTypes = {
  dimensions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  filterOptions: PropTypes.object.isRequired,
  selectedFilters: PropTypes.object.isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  clientColor: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};
